import { Popover } from 'antd';
import React, { PropsWithChildren, useEffect, useMemo, useState } from 'react';
import { Prospect, TPharmacy, TProspectChangeContext } from 'types/common';
import s from './s.module.less';
import difference from 'lodash/difference';
import { markPatientProfileRecordUpdatedReadByVersionId } from 'api/prospect';
import dayjs from 'utils/dayjs';
import { formatDate } from 'utils/common';

interface IProps {
    prospectChangeContext?: TProspectChangeContext;
    prospectData?: Prospect;
    type: 'pharmacy' | 'allergy' | 'medications' | 'medicalHistory';
    onRecordRead?: () => void;
}

const map: Record<string, any> = {
    pharmacy: {
        title: 'Pharmacy',
        updated: 'pharmacyUpdated',
        updatedBy: 'pharmacyUpdatedBy',
        updatedAt: 'pharmacyUpdatedAt',
        oldField: 'oldPharmacy',
        newField: 'pharmacy',
        versionId: 'newPharmacyVersionId',
    },
    allergy: {
        title: 'Allergy',
        updated: 'allergyUpdated',
        updatedBy: 'allergyUpdatedBy',
        updatedAt: 'allergyUpdatedAt',
        oldField: 'oldAllergies',
        newField: 'allergies',
        versionId: 'newAllergyVersionId',
    },
    medications: {
        title: 'Medications',
        updated: 'medicationsUpdated',
        updatedBy: 'medicationsUpdatedBy',
        updatedAt: 'medicationsUpdatedAt',
        oldField: 'oldMedications',
        newField: 'medications',
        versionId: 'newMedicationsVersionId',
    },
    medicalHistory: {
        title: 'Medical history',
        updated: 'medicalHistoryUpdated',
        updatedBy: 'medicalHistoryUpdatedBy',
        updatedAt: 'medicalHistoryUpdatedAt',
        oldField: 'oldMedicalHistory',
        newField: 'medicalHistory',
        versionId: 'newMedicalHistoryVersionId',
    },
};

export default function UpdatedRecordPopover(props: PropsWithChildren<IProps>) {
    const [showMoreAdded, setShowMoreAdded] = useState(false);
    const [showMoreRemoved, setShowMoreRemoved] = useState(false);

    const match = useMemo(() => {
        return map[props.type];
    }, [props.type]);

    const isUpdated = useMemo(() => {
        if (!match || !props.prospectChangeContext) {
            return false;
        }
        return !!props.prospectChangeContext[match.updated as keyof TProspectChangeContext];
    }, [match, props.prospectChangeContext]);

    const updatedBy = useMemo(() => {
        if (!match || !props.prospectChangeContext) {
            return '-';
        }
        return (props.prospectChangeContext[match.updatedBy as keyof TProspectChangeContext] as string) || '-';
    }, [props.prospectChangeContext, match]);

    const updatedAt = useMemo(() => {
        if (!match || !props.prospectChangeContext) {
            return '-';
        }
        const date = props.prospectChangeContext[match.updatedAt as keyof TProspectChangeContext];
        if (!date) {
            return '-';
        }
        return formatDate(dayjs(date as string));
    }, [props.prospectChangeContext, match]);

    const addedAndRemovedData = useMemo(() => {
        if (!match || !props.prospectChangeContext || props.type === 'pharmacy') {
            return {
                added: [],
                removed: [],
            };
        }

        const oldData = props.prospectChangeContext[match.oldField as keyof TProspectChangeContext] as string[];
        const newData = props.prospectData?.[match.newField as keyof Prospect] as string[];

        const added: string[] = difference(newData, oldData);
        const removed: string[] = difference(oldData, newData);
        return { added, removed };
    }, [match, props.prospectChangeContext, props.prospectData]);

    const renderPharmacyContent = () => {
        const renderPharmacy = (pharmacy?: TPharmacy) => {
            return pharmacy
                ? `${pharmacy.name}, ${pharmacy.address}, ${pharmacy.city}, ${pharmacy.state}, ${pharmacy.zipCode}`
                : '-';
        };
        return (
            <div className={s.container}>
                <div className={s.title}>
                    {map[props.type].title} updated by {updatedBy} on {updatedAt}
                </div>

                <div className={s.subtitle}>Previous pharmacy:</div>
                <div className={s.content}>{renderPharmacy(props.prospectChangeContext?.oldPharmacy?.[0])}</div>
                <div className={s.subtitle}>New pharmacy:</div>
                <div className={s.content}>{renderPharmacy(props.prospectData?.pharmacy?.[0])}</div>
            </div>
        );
    };

    const renderListContent = () => {
        const isMoreAddedVisible = addedAndRemovedData.added.length > 5;
        const isMoreRemovedVisible = addedAndRemovedData.removed.length > 5;

        const moreAddedContent =
            !showMoreAdded && isMoreAddedVisible
                ? addedAndRemovedData.added.slice(5).map((item: any) => <div key={item}>{item}</div>)
                : addedAndRemovedData.added.map((item: any) => <div key={item}>{item}</div>);

        const moreRemovedContent =
            !showMoreRemoved && isMoreRemovedVisible
                ? addedAndRemovedData.removed.slice(5).map((item: any) => <div key={item}>{item}</div>)
                : addedAndRemovedData.removed.map((item: any) => <div key={item}>{item}</div>);
        return (
            <div className={s.container}>
                <div className={s.title}>
                    {map[props.type].title} updated by {updatedBy} on {updatedAt}
                </div>
                {addedAndRemovedData.added.length > 0 && (
                    <>
                        <div className={s.subtitle}>Added:</div>
                        <div className={s.content}>{moreAddedContent}</div>
                        {isMoreAddedVisible && (
                            <div className={s.more} onClick={() => setShowMoreAdded(!showMoreAdded)}>
                                {showMoreAdded ? 'Hide' : `View ${addedAndRemovedData.added.length - 5} more`}
                            </div>
                        )}
                    </>
                )}
                {addedAndRemovedData.removed.length > 0 && (
                    <>
                        <div className={s.subtitle}>Removed:</div>
                        <div className={s.content}>{moreRemovedContent}</div>
                        {isMoreRemovedVisible && (
                            <div className={s.more} onClick={() => setShowMoreRemoved(!showMoreRemoved)}>
                                {showMoreRemoved ? 'Hide' : `View ${addedAndRemovedData.removed.length - 5} more`}
                            </div>
                        )}
                    </>
                )}
            </div>
        );
    };

    const renderContent = () => {
        if (props.type === 'pharmacy') {
            return renderPharmacyContent();
        }
        return renderListContent();
    };

    const markRecordUpdatedRead = (open: boolean) => {
        if (open && isUpdated) {
            const versionId = (props.prospectChangeContext as any)?.[map[props.type]?.versionId];
            if ((props.type, versionId)) {
                markPatientProfileRecordUpdatedReadByVersionId(versionId);
            }
        }
        if (!open && isUpdated && props.onRecordRead) {
            props.onRecordRead?.();
        }
    };
    // console.log('type', props.type);
    // console.log('isUpdated', isUpdated);
    // console.log('match', match);

    if (!isUpdated) {
        return <div />;
    }
    return (
        <Popover
            content={renderContent()}
            trigger={['hover', 'click']}
            arrow={false}
            title=""
            onOpenChange={markRecordUpdatedRead}
        >
            <span className={s.updatedBtn}>Updated</span>
            <br />
        </Popover>
    );
}
